import { Extension } from '@tiptap/core'
import { Plugin, PluginKey, Transaction } from '@tiptap/pm/state'
import { ReplaceStep } from '@tiptap/pm/transform'

export const ClearMarksPlugin = Extension.create({
    name: 'clearMarks',

    addProseMirrorPlugins() {
        const editor = this.editor

        return [
            new Plugin({
                key: new PluginKey('clearMarks'),

                appendTransaction: (transactions, oldState, newState) => {
                    // Check if any transaction had deletions
                    let hasDeleted = false
                    let deletionPos = -1

                    transactions.forEach(tr => {
                        if (!tr.docChanged) return

                        tr.steps.forEach((step, index) => {
                            if (step instanceof ReplaceStep) {
                                // Check if this is a deletion (from < to and empty slice)
                                if (step.from < step.to && step.slice.size === 0) {
                                    hasDeleted = true
                                    deletionPos = step.from
                                }
                            }
                        })
                    })

                    if (!hasDeleted || deletionPos === -1) return null

                    const { selection } = newState
                    const { $from, $to } = selection

                    // Get the current marks at the selection
                    const marks = $from.marks()

                    // Check for stored marks (marks that will be applied to next input)
                    const storedMarks = newState.storedMarks || []

                    // Combine both current marks and stored marks
                    const allMarks = [...marks, ...storedMarks]

                    if (allMarks.length === 0) return null

                    // Check if we have any special marks that should be cleared
                    const specialMarks = allMarks.filter(mark => {
                        const markName = mark.type.name

                        // Clear links
                        if (markName === 'link') return true

                        // For bold marks, be more careful about action tags
                        if (markName === 'bold') {
                            // Only clear bold if we're actually in the middle of an incomplete action tag
                            // or if the deletion happened within an action tag
                            try {
                                const textAtCursor = newState.doc.textBetween(
                                    Math.max(0, $from.pos - 5),
                                    Math.min(newState.doc.content.size, $from.pos + 5),
                                )

                                // Check if we're in the middle of an incomplete action tag (e.g., "!abc")
                                // This is more specific than just checking for any action tag pattern
                                const incompleteActionTag = textAtCursor.match(/!\w*$/)
                                const deletedFromActionTag =
                                    textAtCursor.match(/!\w+/) &&
                                    deletionPos >= $from.pos - 10 &&
                                    deletionPos <= $from.pos + 10

                                // Only clear if we're in an incomplete action tag or just deleted from one
                                if (incompleteActionTag || deletedFromActionTag) {
                                    // Double-check: make sure this isn't a complete action tag
                                    const completeActionTag = textAtCursor.match(/!\w+(?:\s|$)/)
                                    return !completeActionTag
                                }
                            } catch (e) {
                                // Ignore range errors
                            }
                        }

                        // Clear marks with meme class
                        if (mark.attrs?.class?.includes('meme')) return true

                        return false
                    })

                    // Also check if we just deleted a mention node
                    let wasNearMention = false
                    try {
                        const resolvedPos = newState.doc.resolve(deletionPos)
                        const nodeBefore = resolvedPos.nodeBefore
                        const nodeAfter = resolvedPos.nodeAfter

                        wasNearMention =
                            (nodeBefore && nodeBefore.type.name === 'mention') ||
                            (nodeAfter && nodeAfter.type.name === 'mention')
                    } catch (e) {
                        // Ignore resolution errors
                    }

                    if (specialMarks.length > 0 || wasNearMention) {
                        let tr = newState.tr

                        // Remove all stored marks
                        storedMarks.forEach(mark => {
                            tr = tr.removeStoredMark(mark)
                        })

                        // Use Tiptap's unsetAllMarks command at the current position
                        if (editor.commands.unsetAllMarks) {
                            // This is cleaner but requires the command to be available
                            tr = tr.setSelection(selection)

                            // Explicitly remove each mark type we care about
                            if (newState.schema.marks.link) {
                                tr = tr.removeMark($from.pos, $to.pos, newState.schema.marks.link)
                            }
                            if (newState.schema.marks.bold) {
                                tr = tr.removeMark($from.pos, $to.pos, newState.schema.marks.bold)
                            }
                        }

                        return tr
                    }

                    return null
                },

                props: {
                    handleKeyDown(view, event) {
                        // Only handle delete/backspace
                        if (event.key !== 'Delete' && event.key !== 'Backspace') {
                            return false
                        }

                        // Don't interfere with IME composition
                        if (view.composing) {
                            return false
                        }

                        // Use requestAnimationFrame for better timing
                        requestAnimationFrame(() => {
                            const { state } = view
                            const { selection } = state
                            const { $from } = selection

                            // Check current marks and stored marks
                            const currentMarks = $from.marks()
                            const storedMarks = state.storedMarks || []
                            const allMarks = [...currentMarks, ...storedMarks]

                            // Look for problematic marks
                            const hasProblematicMark = allMarks.some(mark => {
                                const markType = mark.type.name
                                if (markType === 'link') return true

                                // Be more specific about action tag bold clearing
                                if (markType === 'bold') {
                                    const textContent = $from.parent.textContent
                                    const cursorPos = $from.parentOffset

                                    // Only clear if we're in an incomplete action tag
                                    const beforeCursor = textContent.slice(0, cursorPos)
                                    const afterCursor = textContent.slice(cursorPos)
                                    const fullText = beforeCursor + afterCursor

                                    // Check for incomplete action tag at cursor
                                    const incompletePattern = /!\w*$/
                                    if (beforeCursor.match(incompletePattern)) {
                                        // Make sure it's not actually a complete action tag
                                        const completePattern = /!\w+(?:\s|$)/
                                        return !fullText.match(completePattern)
                                    }
                                }

                                if (mark.attrs?.class?.includes('meme')) return true
                                return false
                            })

                            if (hasProblematicMark) {
                                // Clear marks using editor commands
                                editor.chain().focus().unsetAllMarks().run()
                            }
                        })

                        return false
                    },
                },
            }),
        ]
    },
})

export default ClearMarksPlugin
